import { getContrastColor, Select, tryParseJSONObject } from '@fox/atoms';
import { SelectMeta } from '@fox/data-grid';
import { useCallback, useState } from 'react';
import { useActionCentreContext, useFilterClearListener } from '../action-centre-context';
import { ActionCentreColumn } from '../contracts';

export function SelectFilter({ column }: { column: ActionCentreColumn }) {
	const { setFilter } = useActionCentreContext();
	const [localValue, setLocalValue] = useState<string[]>([]);

	// Listen for filter clear events and reset local state
	useFilterClearListener(
		useCallback(() => {
			setLocalValue([]);
		}, [])
	);

	const meta = column.customFieldMetadata ? tryParseJSONObject<SelectMeta>(column.customFieldMetadata) : undefined;

	function backgroundColor(meta: SelectMeta, value: string) {
		if (!meta.colourCoded || !meta.options) {
			return 'transparent';
		}
		const match = meta.options.find(a => a.id == value || a.label == value);
		if (!match) return 'transparent';
		return match.colour ?? 'transparent';
	}

	const options =
		meta?.options?.map(o => {
			const bgColor = backgroundColor(meta, o.id);
			return {
				label: o.label,
				style: {
					backgroundColor: bgColor,
					color: getContrastColor(bgColor)
				},
				value: o.id
			};
		}) ?? [];

	const handleChange = useCallback(
		(_, v: typeof options) => {
			const newValue = v.map(s => s?.value);
			setLocalValue(newValue);

			if (newValue.length > 0) {
				setFilter(JSON.stringify({ includes: newValue }), column);
			} else {
				setFilter(undefined, column);
			}
		},
		[setFilter, column]
	);

	return (
		<Select<{ label: string; value: string; style: { backgroundColor: string; color: string } }, true, false, false>
			options={options?.filter(option => !localValue?.includes(option.value))}
			value={localValue?.map(s => options.find(o => o.value === s)) ?? []}
			multiple={true}
			getOptionLabel={o => o?.label}
			placeholder={column.title}
			size="small"
			onChange={handleChange}
		/>
	);
}
